import {
  forwardRef,
  PropsWithChildren,
  RefObject,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { css } from "src/styles/theme";
import { InfiniteCanvasView } from "../types/canvas.types";
import { InfiniteCanvasBackground } from "./infinite-canvas-background";

export interface InfiniteCanvasProps {
  view: InfiniteCanvasView;
  onChange: (view: InfiniteCanvasView) => void;
}

export const InfiniteCanvas = forwardRef<
  HTMLDivElement | null,
  PropsWithChildren<InfiniteCanvasProps>
>(({ view, onChange, children }, ref) => {
  const canvasRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => canvasRef.current!);

  useWheel(canvasRef, view, onChange);
  useTouch(canvasRef, view, onChange);

  return (
    <div className={$canvas()} ref={canvasRef}>
      <InfiniteCanvasBackground x={view.x} y={view.y} />
      <div
        className={$transformCanvas()}
        style={{ transform: `translate(${-view.x}px, ${-view.y}px)` }}
      >
        {children}
      </div>
    </div>
  );
});

function useWheel(
  canvasRef: RefObject<HTMLElement>,
  view: InfiniteCanvasView,
  onChange: (view: InfiniteCanvasView) => void
) {
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const canvas = canvasRef.current!;
    const wheelHandler = (event: WheelEvent) => {
      event.preventDefault();
      onChange({
        x: viewRef.current.x + event.deltaX,
        y: viewRef.current.y + event.deltaY,
      });
    };

    canvas.addEventListener("wheel", wheelHandler, { passive: false });
    return () => canvas.removeEventListener("wheel", wheelHandler);
  }, [onChange, canvasRef]);
}

function useTouch(
  canvasRef: RefObject<HTMLElement>,
  view: InfiniteCanvasView,
  onChange: (view: InfiniteCanvasView) => void
) {
  const touchRef = useRef<{ x: number; y: number } | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  useEffect(() => {
    const canvas = canvasRef.current!;

    const touchendHandler = () => (touchRef.current = null);
    const touchstartHandler = (event: TouchEvent) => {
      if (event.currentTarget === event.target) {
        const { clientX: x, clientY: y } = event.changedTouches[0];
        touchRef.current = { x, y };
      }
    };

    const touchmoveHandler = (event: TouchEvent) => {
      if (!touchRef.current) return;
      event.preventDefault();

      const { clientX, clientY } = event.changedTouches[0];
      const diffX = clientX - touchRef.current.x;
      const diffY = clientY - touchRef.current.y;
      const x = viewRef.current.x - diffX;
      const y = viewRef.current.y - diffY;

      touchRef.current.x = clientX;
      touchRef.current.y = clientY;

      onChange({ x, y });
    };

    canvas.addEventListener("touchstart", touchstartHandler);
    canvas.addEventListener("touchend", touchendHandler);
    canvas.addEventListener("touchmove", touchmoveHandler);

    return () => {
      canvas.removeEventListener("touchstart", touchstartHandler);
      canvas.removeEventListener("touchend", touchendHandler);
      canvas.removeEventListener("touchmove", touchmoveHandler);
    };
  }, [onChange, canvasRef]);
}

const $canvas = css({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: "hidden",
});

const $transformCanvas = css({
  position: "absolute",
  top: 0,
  left: 0,
});
